import { clinicService } from '../services/api/clinicService';
import { decodeToken, getClinicIdFromToken } from './jwtUtils';
import { storage } from './storage';

/**
 * Resolve the signed-in user's clinic id from stored auth, registration
 * status, or the JWT. Login historically dropped clinic_name and sometimes
 * clinic_id; the token is the most reliable fallback.
 */
export function resolveClinicId(): number | undefined {
  const authData = storage.getAuthData();
  if (!authData) return undefined;

  if (authData.user?.clinic_id) {
    return authData.user.clinic_id;
  }
  if (authData.registration_status?.clinic_id) {
    return authData.registration_status.clinic_id;
  }
  if (authData.access_token) {
    return getClinicIdFromToken(authData.access_token) ?? undefined;
  }
  return undefined;
}

export function getStoredClinicName(): string | null {
  const authData = storage.getAuthData();
  const stored = authData?.user?.clinic_name;
  if (typeof stored === 'string' && stored.trim()) {
    return stored.trim();
  }
  if (authData?.access_token) {
    const fromToken = decodeToken(authData.access_token)?.clinic_name;
    if (typeof fromToken === 'string' && fromToken.trim()) {
      return fromToken.trim();
    }
  }
  return null;
}

export function persistClinicName(name: string): void {
  const fresh = storage.getAuthData();
  if (!fresh?.user) return;
  fresh.user.clinic_name = name;
  storage.setAuthData(fresh);
}

/**
 * Return a display clinic name, preferring the stored auth value, then the
 * auth-service clinic profile, then EHR branding display_name.
 * Medical officers may not be allowed to read /clinics/:id on the auth
 * service, so branding is the fallback.
 */
export async function resolveClinicName(): Promise<string | null> {
  const stored = getStoredClinicName();
  if (stored) return stored;

  const clinicId = resolveClinicId();
  if (!clinicId) return null;

  try {
    const profile = await clinicService.getClinicProfile(clinicId);
    if (profile?.name?.trim()) {
      const name = profile.name.trim();
      persistClinicName(name);
      return name;
    }
  } catch {
    // Profile read is admin-oriented on the auth service.
  }

  try {
    const branding = await clinicService.getClinicBranding();
    if (branding?.display_name?.trim()) {
      const name = branding.display_name.trim();
      persistClinicName(name);
      return name;
    }
  } catch {
    // Branding is optional and may be unset.
  }

  return null;
}
